import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { VerificationCodeInput } from '@/components/auth/VerificationCodeInput';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Mail, Eye, EyeOff, Clock } from 'lucide-react';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { api } from '@/lib/api';

export default function AuthPage() {
  const navigate = useNavigate();
  const { login, verify2FA, resend2FA, twoFactorPending, cancelTwoFactor } = useAuth();
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [codeError, setCodeError] = useState(false);

  // Two-step login flow state
  const [loginStep, setLoginStep] = useState<'email' | 'password' | 'setup-password'>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [pendingApproval, setPendingApproval] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [emailChecked, setEmailChecked] = useState<{
    exists: boolean;
    hasPassword: boolean;
    status?: 'pending' | 'approved' | 'rejected';
    message?: string;
  } | null>(null);
  const [setupEmailSent, setSetupEmailSent] = useState(false);

  // Cooldown timer for resend
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // ===== STEP 1: CHECK EMAIL =====
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError('');

    if (!email.trim()) {
      setEmailError('Email is required');
      return;
    }

    // Basic email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setPendingApproval(null);
    setLoading(true);
    try {
      const result = await api.post<{
        exists: boolean;
        hasPassword: boolean;
        status?: 'pending' | 'approved' | 'rejected';
        message?: string;
      }>('/auth/check-email', { email: email.toLowerCase() });

      if (!result.exists) {
        // User not pre-registered
        setEmailError(result.message || 'If not pre-registered, contact converge@npsfoundation.org');
      } else if (result.status === 'pending') {
        // Account exists but is awaiting admin approval — show info panel, not an error
        setPendingApproval(result.message || 'Your registration is pending approval. You will be notified when your account is approved.');
      } else if (result.status === 'rejected') {
        setEmailError(result.message || 'Your account is not approved for this event.');
      } else if (!result.hasPassword) {
        // User is pre-registered but has no password - show setup screen
        setEmailChecked(result);
        setLoginStep('setup-password');
      } else {
        // User exists with password - proceed to password step
        setEmailChecked(result);
        setLoginStep('password');
      }
    } catch (error: any) {
      console.error('Email check error:', error);
      const errorMessage = error.response?.data?.error?.message || 'An error occurred. Please try again.';
      setEmailError(errorMessage);
      toast.error('Error', { description: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  // ===== STEP 2: LOGIN WITH PASSWORD =====
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');

    if (!password) {
      setPasswordError('Password is required');
      return;
    }

    setLoading(true);
    try {
      const response = await login({ email, password });

      if (response.requires2FA) {
        toast.success('Verification code sent!', {
          description: 'Please check your email for the 6-digit code.',
        });
        setResendCooldown(30);
      } else {
        toast.success('Welcome back!', {
          description: "You're now logged in.",
        });
        navigate('/');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.error?.message || 'Invalid email or password';

      if (error.response?.data?.error?.code === 'TWO_FACTOR_RATE_LIMIT') {
        const cooldown = error.response?.data?.error?.cooldownRemaining || 30;
        setResendCooldown(cooldown);
        toast.error('Please wait', {
          description: `You can request a new code in ${cooldown} seconds.`,
        });
      } else {
        toast.error('Login failed', {
          description: errorMessage,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // ===== GO BACK TO EMAIL STEP =====
  const handleBackToEmail = () => {
    setLoginStep('email');
    setPassword('');
    setPasswordError('');
    setEmailChecked(null);
  };

  // ===== SETUP PASSWORD: TRIGGER FORGOT PASSWORD EMAIL =====
  const handleSetupPassword = async () => {
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: email.toLowerCase() });
      toast.success('Email sent!', {
        description: 'Check your inbox for the password setup link.',
      });
      setSetupEmailSent(true);
    } catch (error: any) {
      console.error('Setup password error:', error);
      toast.error('Failed to send email', {
        description: error.response?.data?.error?.message || 'Please try again later.',
      });
    } finally {
      setLoading(false);
    }
  };

  // ===== 2FA: VERIFY CODE =====
  const handleVerifyCode = async (code: string) => {
    if (!twoFactorPending) return;

    setLoading(true);
    setCodeError(false);
    try {
      await verify2FA(twoFactorPending.userId, code);
      toast.success('Welcome back!', {
        description: "You're now logged in.",
      });
      navigate('/');
    } catch (error: any) {
      console.error('2FA verification error:', error);
      setCodeError(true);
      const errorMessage = error.response?.data?.error?.message || 'Invalid verification code';
      toast.error('Verification failed', { description: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  // ===== 2FA: RESEND CODE =====
  const handleResendCode = async () => {
    if (!twoFactorPending || resendCooldown > 0) return;

    setLoading(true);
    try {
      await resend2FA(twoFactorPending.userId);
      setResendCooldown(30);
      toast.success('Code resent!', {
        description: 'Please check your email for the new code.',
      });
    } catch (error: any) {
      console.error('Resend error:', error);
      const cooldown = error.response?.data?.error?.cooldownRemaining || 30;
      setResendCooldown(cooldown);
      toast.error('Failed to resend', {
        description: error.response?.data?.error?.message || 'Please try again later.',
      });
    } finally {
      setLoading(false);
    }
  };

  // ===== 2FA: CANCEL AND GO BACK =====
  const handleBack2FA = () => {
    cancelTwoFactor();
    setCodeError(false);
  };

  // ===== RENDER 2FA VERIFICATION SCREEN =====
  if (twoFactorPending) {
    return (
      <div
        className="relative min-h-screen flex items-center justify-center p-3 md:p-4 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: 'url("/AI Human-Machine.jpg")', backgroundSize: 'cover', backgroundPosition: 'center 70%' }}
      >
                <Card className="w-full max-w-md relative z-10 bg-background/95 dark:bg-background/98 backdrop-blur-sm">
          <CardHeader className="text-center px-4 md:px-6 py-4 md:py-6">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <Mail className="w-8 h-8 text-primary" />
            </div>
            <CardTitle className="text-xl md:text-2xl">Check your email</CardTitle>
            <CardDescription className="text-sm md:text-base">
              We sent a verification code to
              <br />
              <span className="font-medium text-foreground">{twoFactorPending.email}</span>
            </CardDescription>
          </CardHeader>
          <CardContent className="px-4 md:px-6 pb-4 md:pb-6 space-y-6">
            <div className="space-y-4">
              <Label className="text-center block text-sm text-muted-foreground">
                Enter the 6-digit code
              </Label>
              <VerificationCodeInput
                onComplete={handleVerifyCode}
                disabled={loading}
                error={codeError}
              />
              {codeError && (
                <p className="text-center text-sm text-destructive">
                  Invalid code. Please try again.
                </p>
              )}
              <p className="text-center text-xs text-muted-foreground">
                Code expires in 5 minutes
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <Button
                variant="ghost"
                onClick={handleResendCode}
                disabled={loading || resendCooldown > 0}
                className="text-sm"
              >
                {resendCooldown > 0
                  ? `Resend code in ${resendCooldown}s`
                  : "Didn't receive the code? Resend"}
              </Button>

              <Button
                variant="outline"
                onClick={handleBack2FA}
                disabled={loading}
                className="gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ===== RENDER EMAIL STEP =====
  if (loginStep === 'email') {
    return (
      <div
        className="relative min-h-screen flex items-center justify-center p-3 md:p-4 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: 'url("/AI Human-Machine.jpg")', backgroundSize: 'cover', backgroundPosition: 'center 70%' }}
      >
                <Card className="w-full max-w-md relative z-10 bg-background/95 dark:bg-background/98 backdrop-blur-sm">
          <CardHeader className="text-center px-4 md:px-6 py-4 md:py-6">
            <CardTitle className="text-xl md:text-2xl">Converge @ NPS</CardTitle>
            <CardDescription className="text-sm md:text-base">
              Enter your email to continue
            </CardDescription>
          </CardHeader>
          <CardContent className="px-4 md:px-6 pb-4 md:pb-6">
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm md:text-base">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailError('');
                    setPendingApproval(null);
                  }}
                  disabled={loading}
                  maxLength={255}
                  autoFocus
                  className={`h-11 md:h-10 text-sm md:text-base ${emailError ? 'border-destructive' : ''}`}
                />
                {emailError && (
                  <p className="text-xs md:text-sm text-destructive">
                    {emailError.includes('converge@npsfoundation.org') ? (
                      <>
                        If not pre-registered, contact{' '}
                        <a
                          href="mailto:converge@npsfoundation.org"
                          className="underline hover:no-underline"
                        >
                          converge@npsfoundation.org
                        </a>
                      </>
                    ) : (
                      emailError
                    )}
                  </p>
                )}
                {pendingApproval && (
                  <Alert className="border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
                    <Clock className="h-4 w-4 !text-amber-600 dark:!text-amber-400" />
                    <AlertTitle>Registration Pending Admin Approval</AlertTitle>
                    <AlertDescription>{pendingApproval}</AlertDescription>
                  </Alert>
                )}
              </div>
              <Button
                type="submit"
                className="w-full h-11 md:h-10 text-sm md:text-base gap-2"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Checking...
                  </>
                ) : (
                  'Continue'
                )}
              </Button>
            </form>
            <p className="text-xs md:text-sm text-muted-foreground text-center mt-4">
              Not registered for this event yet?{' '}
              <Link to="/register" className="text-primary hover:underline">
                Register here
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ===== RENDER SETUP PASSWORD STEP (for pre-registered users) =====
  if (loginStep === 'setup-password') {
    // Show confirmation after email is sent
    if (setupEmailSent) {
      return (
        <div
          className="relative min-h-screen flex items-center justify-center p-3 md:p-4 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: 'url("/AI Human-Machine.jpg")', backgroundSize: 'cover', backgroundPosition: 'center 70%' }}
        >
          <Card className="w-full max-w-md relative z-10 bg-background/95 dark:bg-background/98 backdrop-blur-sm">
            <CardHeader className="text-center px-4 md:px-6 py-4 md:py-6">
              <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Mail className="w-8 h-8 text-primary" />
              </div>
              <CardTitle className="text-xl md:text-2xl">Check your email</CardTitle>
              <CardDescription className="text-sm md:text-base">
                We sent a password setup link to
                <br />
                <span className="font-medium text-foreground">{email}</span>
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4 md:px-6 pb-4 md:pb-6 space-y-4">
              <p className="text-xs text-muted-foreground text-center">
                The email may take 10+ minutes to arrive in your inbox.
              </p>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setSetupEmailSent(false);
                  handleBackToEmail();
                }}
                className="w-full gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Use a different email
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <div
        className="relative min-h-screen flex items-center justify-center p-3 md:p-4 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: 'url("/AI Human-Machine.jpg")', backgroundSize: 'cover', backgroundPosition: 'center 70%' }}
      >
        <Card className="w-full max-w-md relative z-10 bg-background/95 dark:bg-background/98 backdrop-blur-sm">
          <CardHeader className="text-center px-4 md:px-6 py-4 md:py-6">
            <CardTitle className="text-xl md:text-2xl">Converge @ NPS</CardTitle>
            <CardDescription className="text-sm md:text-base">
              {email}
            </CardDescription>
          </CardHeader>
          <CardContent className="px-4 md:px-6 pb-4 md:pb-6 space-y-4">
            <Button
              onClick={handleSetupPassword}
              className="w-full h-11 md:h-10 text-sm md:text-base gap-2"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send password reset email'
              )}
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              The reset instructions email may take 10+ minutes to arrive in your inbox.
            </p>
            <Button
              type="button"
              variant="ghost"
              onClick={handleBackToEmail}
              disabled={loading}
              className="w-full gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Use a different email
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ===== RENDER PASSWORD STEP =====
  return (
    <div
      className="relative min-h-screen flex items-center justify-center p-3 md:p-4 bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: 'url("/AI Human-Machine.jpg")', backgroundSize: 'cover', backgroundPosition: 'center 70%' }}
    >
            <Card className="w-full max-w-md relative z-10 bg-background/95 dark:bg-background/98 backdrop-blur-sm">
        <CardHeader className="text-center px-4 md:px-6 py-4 md:py-6">
          <CardTitle className="text-xl md:text-2xl">Converge @ NPS</CardTitle>
          <CardDescription className="text-sm md:text-base">
            {email}
          </CardDescription>
        </CardHeader>
        <CardContent className="px-4 md:px-6 pb-4 md:pb-6">
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm md:text-base">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setPasswordError('');
                  }}
                  disabled={loading}
                  autoFocus
                  className={`h-11 md:h-10 text-sm md:text-base pr-10 ${passwordError ? 'border-destructive' : ''}`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {passwordError && (
                <p className="text-xs md:text-sm text-destructive">{passwordError}</p>
              )}
            </div>

            <div className="text-right">
              <Link
                to="/forgot-password"
                className="text-xs md:text-sm text-primary hover:underline"
              >
                {emailChecked?.hasPassword ? 'Forgot Password?' : 'Set up your password'}
              </Link>
            </div>

            <Button
              type="submit"
              className="w-full h-11 md:h-10 text-sm md:text-base gap-2"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Logging in...
                </>
              ) : (
                'Login'
              )}
            </Button>

            <Button
              type="button"
              variant="ghost"
              onClick={handleBackToEmail}
              disabled={loading}
              className="w-full gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Use a different email
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
